import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Download, Eye, KeyRound, Loader2, Lock, Pencil, Play, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EdytorSql } from '@/components/EdytorSql';
import { SiatkaEdycji, drogaRozsylki, powodBrakuEdycji } from '@/components/SiatkaEdycji';
import { StanWysylki } from '@/components/StanWysylki';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { fetchCatalog } from '@/lib/authoring-api';
import {
  czekajNaWynik,
  fetchWezlyBazy,
  wyrownaj,
  zlecSchemat,
  zlecZapytanie,
  type TabelaSchematu,
  type WezelBazy,
  type WynikZapytania,
} from '@/lib/baza-api';
import { useI18n } from '@/lib/i18n';
import { mozliweNaprawy, wlascicielWiersza, type Kierunek, type Naprawa } from '@/lib/naprawy';
import {
  porownajWyniki,
  tabelaZapytania,
  type Porownanie,
  type StanWiersza,
  type WierszPorownania,
} from '@/lib/porownanie-wynikow';
import {
  kolejnosc,
  kolejnoscWyniku,
  kolumnaSortowania,
  nastepneSortowanie,
  porownajWartosci,
  RANGA_STANU,
  type Sortowanie,
} from '@/lib/sortowanie-wyniku';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  „DZIAŁANIA NA BAZIE" → ZAPYTANIA — odczyt bazy wyspy jak w SSMS (etap 1, 28.09.2026).
 *
 *  ⚠️⚠️ ZAMÓWIONE: „chciałbym, żeby działało to trochę jak SSMS … nie można zrobić selecta".
 *  Decyzje: wolny SQL, zapytanie zwykłym zadaniem agenta (1–3 s), dostęp dla adminów, bez
 *  maskowania. ZAPIS przyjdzie w etapie 2 i pójdzie wyłącznie siatką przez bramkę, na całą wyspę.
 *
 *  ⚠️⚠️ PORÓWNANIE Z DRUGĄ BAZĄ JEST TU NARZĘDZIEM PRZECIW ROZJAZDOM, nie ozdobą: „spoko, że
 *  możemy zrobić selecta na bazie na maszynie, ale też musi być tak, żeby potem nie było
 *  rozjazdów". To samo zapytanie idzie na obie bazy, a każdy wiersz mówi, czy się zgadza.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Ile wierszy rysujemy w siatce — reszta jest w eksporcie. Agent i tak oddaje najwyżej tysiąc. */
const WIERSZY_W_SIATCE = 1000;

type Odpowiedz = { nodeId: string; wynik?: WynikZapytania; blad?: string };

function opisWezla(w: WezelBazy, t: (k: string) => string): string {
  const nazwa = w.automat !== null ? `${w.nodeId} · ${w.automat.nazwa}` : w.nodeId;
  if (!w.obsluguje) return `${nazwa} — ${t('baza.zaStary').replace('{wersja}', w.wersja ?? '—')}`;
  return w.master ? `${nazwa} (${t('baza.master')})` : nazwa;
}

/** Zapytanie „Top 1000" z kolejnością po kluczu — ten sam wycinek na obu bazach przy porównaniu. */
function top1000(t: TabelaSchematu): string {
  const klucz = t.kolumny.filter((k) => k.klucz).map((k) => `[${k.nazwa}]`);
  return `SELECT TOP 1000 * FROM [${t.nazwa}]${klucz.length > 0 ? ` ORDER BY ${klucz.join(', ')}` : ''}`;
}

/** Eksport do CSV ze średnikiem i BOM — tak otwiera się poprawnie w polskim Excelu. */
function pobierzCsv(w: WynikZapytania, nazwa: string): void {
  const pole = (v: string | null): string => (v === null ? '' : /[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const linie = [w.kolumny.map((k) => pole(k.nazwa)).join(';'), ...w.wiersze.map((r) => r.map(pole).join(';'))];
  const url = URL.createObjectURL(new Blob([`\uFEFF${linie.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nazwa;
  a.click();
  URL.revokeObjectURL(url);
}

const KOLOR_STANU: Record<StanWiersza, string> = {
  ZGODNY: '',
  INNY: 'bg-[var(--warning)]/10',
  TYLKO_TU: 'bg-[var(--success)]/10',
  BRAK_TU: 'bg-[var(--destructive)]/10 text-muted-foreground',
};

function DrzewoTabel({
  tabele,
  laduje,
  blad,
  onTop,
  onWstaw,
  onOdswiez,
}: {
  tabele: TabelaSchematu[];
  laduje: boolean;
  blad: string | null;
  onTop: (t: TabelaSchematu) => void;
  onWstaw: (tekst: string) => void;
  onOdswiez: () => void;
}) {
  const { t } = useI18n();
  const [fraza, setFraza] = useState('');
  const [otwarte, setOtwarte] = useState<string | null>(null);
  const widoczne = tabele.filter((x) => x.nazwa.toLowerCase().includes(fraza.trim().toLowerCase()));

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center gap-1">
        <Input value={fraza} placeholder={t('baza.szukaj')} onChange={(e) => setFraza(e.target.value)} className="h-8 text-xs" />
        <Button variant="ghost" size="icon" title={t('baza.odswiezSchemat')} aria-label={t('baza.odswiezSchemat')} onClick={onOdswiez}>
          <RefreshCw className="size-3.5" />
        </Button>
      </div>
      {laduje && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> {t('baza.czytamSchemat')}
        </p>
      )}
      {blad !== null && <p className="text-xs text-[var(--destructive)]">{blad}</p>}
      <ul className="max-h-[32rem] space-y-0.5 overflow-y-auto pr-1 text-xs">
        {widoczne.map((x) => (
          <li key={x.nazwa}>
            <div className="group flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent">
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-1 text-left"
                onClick={() => setOtwarte(otwarte === x.nazwa ? null : x.nazwa)}
              >
                {otwarte === x.nazwa ? <ChevronDown className="size-3 shrink-0" /> : <ChevronRight className="size-3 shrink-0" />}
                <span className="truncate font-mono">{x.nazwa}</span>
                {x.widok && <Eye className="size-3 shrink-0 text-muted-foreground" aria-label={t('baza.widok')} />}
                {x.zapis === 'BRAMKA' && <Pencil className="size-3 shrink-0 text-muted-foreground" aria-label={t('baza.zapisBramka')} />}
                {x.zapis === 'ZAKAZ' && <Lock className="size-3 shrink-0 text-muted-foreground" aria-label={t('baza.zapisZakaz')} />}
              </button>
              <button
                type="button"
                className="shrink-0 rounded px-1 text-[11px] text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100"
                onClick={() => onTop(x)}
              >
                {t('baza.top')}
              </button>
            </div>
            {otwarte === x.nazwa && (
              <ul className="mb-1 ml-4 space-y-0.5">
                {x.kolumny.map((k) => (
                  <li key={k.nazwa}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-1 rounded px-1 text-left hover:bg-accent"
                      title={t('baza.wstawKolumne')}
                      onClick={() => onWstaw(k.nazwa)}
                    >
                      {k.klucz && <KeyRound className="size-3 shrink-0 text-[var(--warning)]" aria-label={t('baza.klucz')} />}
                      <span className="truncate font-mono">{k.nazwa}</span>
                      <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{k.typ}</span>
                      {k.zapisywalna && <Pencil className="size-3 shrink-0 text-muted-foreground" aria-label={t('baza.zapisBramka')} />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ZapytaniaBazy({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const [agentId, setAgentId] = useState('');
  const [porownanieZ, setPorownanieZ] = useState('');
  const [sql, setSql] = useState('');
  const [czeka, setCzeka] = useState(false);
  const [tu, setTu] = useState<Odpowiedz | null>(null);
  const [tam, setTam] = useState<Odpowiedz | null>(null);
  const [bladZlecenia, setBladZlecenia] = useState<string | null>(null);
  /** Treść, która dała bieżący wynik — edytor mógł się od tego czasu zmienić. */
  const [sqlWyniku, setSqlWyniku] = useState('');
  const [edycja, setEdycja] = useState(false);
  /** Operacje wysłane z tego ekranu (siatka albo naprawy) — po nich pasek pokazuje stan na węzłach. */
  const [wyslane, setWyslane] = useState<string[]>([]);
  /**
   * Hurt, w którym część operacji weszła do kolejki, a któraś z kolejnych nie — powód z serwera.
   * ⚠️ TRZYMANY OBOK `wyslane`, nie w dymku: te, które weszły, i tak pójdą na wyspę, więc człowiek musi
   * wiedzieć, której reszty brakuje, zanim złoży ją ponownie.
   */
  const [bladWysylki, setBladWysylki] = useState<string | null>(null);
  /** Sortowanie po nagłówku; `null` = kolejność z bazy. Reguły: `sortowanie-wyniku.ts`. */
  const [sort, setSort] = useState<Sortowanie | null>(null);
  /** Naprawy z porównania — po indeksie wiersza porównania; jedna na wiersz (kierunek do wyboru). */
  const [naprawy, setNaprawy] = useState<Record<number, Naprawa>>({});
  const [potwierdzNaprawy, setPotwierdzNaprawy] = useState(false);
  const [wysylaNaprawy, setWysylaNaprawy] = useState(false);
  const [bladNapraw, setBladNapraw] = useState<string | null>(null);
  const qc = useQueryClient();
  const edytor = useRef<HTMLTextAreaElement>(null);
  /** Numer przebiegu — wynik starszego „Wykonaj" nie może nadpisać nowszego. */
  const przebieg = useRef(0);

  const wezly = useQuery({ queryKey: ['baza', 'wezly', groupId], queryFn: () => fetchWezlyBazy(groupId) });
  const lista = wezly.data?.wezly ?? [];

  /** Domyślnie master — w Smart Syncu na nim się pracuje; w trybie zwykłym jedyna baza wyspy. */
  useEffect(() => {
    if (agentId === '' && lista.length > 0) setAgentId((lista.find((w) => w.obsluguje) ?? lista[0]!).id);
  }, [agentId, lista]);

  const wybrany = lista.find((w) => w.id === agentId) ?? null;
  const drugi = lista.find((w) => w.id === porownanieZ) ?? null;

  const schemat = useQuery({
    queryKey: ['baza', 'schemat', groupId, agentId],
    enabled: wybrany?.obsluguje === true,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { zadanieId } = await zlecSchemat(groupId, agentId);
      const s = await czekajNaWynik(groupId, zadanieId);
      if (s.stan === 'BLAD') throw new Error(s.blad);
      return s.stan === 'GOTOWE' ? (s.schemat ?? []) : [];
    },
  });
  const tabele = schemat.data ?? [];

  /** Katalog edytora — ten sam, co w zakładce „Zmiany": to on mówi, co da się zmienić przez bramkę. */
  const katalog = useQuery({ queryKey: ['authoring', 'catalog', groupId], queryFn: () => fetchCatalog(groupId) });

  const zapytajJedna = async (id: string, tresc: string): Promise<Odpowiedz> => {
    const nodeId = lista.find((w) => w.id === id)?.nodeId ?? '?';
    try {
      const { zadanieId } = await zlecZapytanie(groupId, id, tresc);
      const s = await czekajNaWynik(groupId, zadanieId);
      if (s.stan === 'BLAD') return { nodeId: s.nodeId, blad: s.blad };
      return s.stan === 'GOTOWE' && s.wynik !== undefined ? { nodeId: s.nodeId, wynik: s.wynik } : { nodeId, blad: t('common.error') };
    } catch (e) {
      return { nodeId, blad: e instanceof ApiError || e instanceof Error ? e.message : t('common.error') };
    }
  };

  const wykonaj = async (tresc: string = sql): Promise<void> => {
    if (tresc.trim() === '' || wybrany === null || czeka) return;
    const nr = ++przebieg.current;
    setCzeka(true);
    setBladZlecenia(null);
    setTu(null);
    setTam(null);
    setEdycja(false);
    /** „Odśwież wynik" po wysyłce zostawia stan na węzłach — nowe zapytanie już nie. */
    if (tresc !== sqlWyniku) {
      setWyslane([]);
      setBladWysylki(null);
    }
    setNaprawy({});
    setPotwierdzNaprawy(false);
    setBladNapraw(null);
    setSqlWyniku(tresc);
    try {
      const [a, b] = await Promise.all([
        zapytajJedna(agentId, tresc),
        drugi !== null && drugi.id !== agentId ? zapytajJedna(drugi.id, tresc) : Promise.resolve(null),
      ]);
      if (nr !== przebieg.current) return;
      setTu(a);
      setTam(b);
    } catch (e) {
      if (nr === przebieg.current) setBladZlecenia(e instanceof Error ? e.message : t('common.error'));
    } finally {
      if (nr === przebieg.current) setCzeka(false);
    }
  };

  const wstaw = (tekst: string): void => {
    const el = edytor.current;
    if (el === null) {
      setSql((s) => `${s}${tekst}`);
      return;
    }
    const a = el.selectionStart;
    const b = el.selectionEnd;
    setSql((s) => `${s.slice(0, a)}${tekst}${s.slice(b)}`);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + tekst.length, a + tekst.length);
    });
  };

  const porownanie: Porownanie | null = useMemo(() => {
    if (tu?.wynik === undefined || tam?.wynik === undefined) return null;
    return porownajWyniki(tu.wynik, tam.wynik, tabelaZapytania(sqlWyniku, tabele));
  }, [tu, tam, sqlWyniku, tabele]);

  /** Wiersze siatki odczytu — przy porównaniu z jego stanem, bez niego same wiersze wyniku. */
  const wierszeWidoku: WierszPorownania[] = useMemo(
    () => (porownanie?.ok === true ? porownanie.wiersze : (tu?.wynik?.wiersze ?? []).map((w) => ({ stan: 'ZGODNY' as const, wiersz: w }))),
    [porownanie, tu],
  );
  /**
   * ⚠️ KOLEJNOŚĆ TO INDEKSY WIERSZY, NIE POSORTOWANA KOPIA — naprawy (`naprawy[i]`) i edycja
   * trzymają się indeksu z wyniku, więc zmiana musi trafić w wiersz, który człowiek kliknął.
   */
  const kolejnoscWidoku = useMemo(() => {
    const ile = wierszeWidoku.length;
    if (sort?.kolumna === 'STAN') {
      if (porownanie?.ok !== true) return kolejnosc(ile, null);
      return kolejnosc(ile, (a, b) => RANGA_STANU[wierszeWidoku[a]!.stan] - RANGA_STANU[wierszeWidoku[b]!.stan], sort.malejaco);
    }
    const kolumny = tu?.wynik?.kolumny ?? [];
    const c = kolumnaSortowania(sort, kolumny);
    if (c === null) return kolejnosc(ile, null);
    const typ = kolumny[c]!.typ;
    return kolejnosc(
      ile,
      (a, b) => porownajWartosci(wierszeWidoku[a]!.wiersz[c] ?? null, wierszeWidoku[b]!.wiersz[c] ?? null, typ),
      sort!.malejaco,
    );
  }, [sort, porownanie, wierszeWidoku, tu]);

  if (wezly.isPending) return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
  if (lista.length === 0) return <p className="text-sm text-muted-foreground">{t('baza.brakBaz')}</p>;

  const wynik = tu?.wynik ?? null;
  const nazwaTabeli = tabelaZapytania(sqlWyniku, tabele)?.nazwa ?? null;
  const tabelaKatalogu =
    nazwaTabeli === null ? null : (katalog.data?.tables.find((x) => x.name.toLowerCase() === nazwaTabeli.toLowerCase()) ?? null);
  const powodBrakuEdycjiWyniku = wynik === null ? null : powodBrakuEdycji(wynik, tabelaKatalogu, nazwaTabeli);
  /**
   * ⚠️ NAPRAWY TYLKO PRZY PORÓWNANIU PO KLUCZU I DLA TABELI, KTÓRĄ OTWIERA EDYTOR — bez klucza
   * nie wiadomo, który wiersz poprawić w pozostałych bazach, a spoza bramki nie da się pisać.
   */
  const tabelaSchematu = nazwaTabeli === null ? null : (tabele.find((x) => x.nazwa === nazwaTabeli) ?? null);
  const mozeNaprawiac =
    porownanie?.ok === true &&
    porownanie.poKluczu &&
    tabelaKatalogu !== null &&
    tabelaSchematu !== null &&
    wybrany !== null &&
    drugi !== null &&
    wynik !== null;
  const naprawyDla = (w: WierszPorownania): Naprawa[] =>
    mozeNaprawiac
      ? mozliweNaprawy(w, { wynik: wynik!, tabela: tabelaSchematu!, tu: wybrany!, tam: drugi!, wezly: lista, akcje: tabelaKatalogu!.actions })
      : [];
  const wybierzWszystkie = (kierunek: Kierunek): void => {
    if (porownanie?.ok !== true) return;
    const nowe: Record<number, Naprawa> = {};
    porownanie.wiersze.forEach((w, i) => {
      const n = naprawyDla(w).find((x) => x.kierunek === kierunek);
      if (n !== undefined) nowe[i] = n;
    });
    setNaprawy(nowe);
    setPotwierdzNaprawy(false);
  };
  const wyslijNaprawy = async (): Promise<void> => {
    if (tabelaKatalogu === null) return;
    setWysylaNaprawy(true);
    setBladNapraw(null);
    /** ⚠️ Poza `try`: gdy drugie źródło odmówi, pierwsza operacja już jedzie — jej stan też ma być widać. */
    const opIds: string[] = [];
    try {
      const wgZrodla = new Map<string, Naprawa[]>();
      for (const n of Object.values(naprawy)) wgZrodla.set(n.zrodlo.id, [...(wgZrodla.get(n.zrodlo.id) ?? []), n]);
      let poprawek = 0;
      let dolozen = 0;
      let usuniec = 0;
      /** ⚠️ Jedno źródło na operację — dwa kierunki to dwie decyzje i dwa wpisy w rejestrze. */
      for (const [zrodloId, lista_] of wgZrodla) {
        const r = await wyrownaj(groupId, tabelaKatalogu.name, zrodloId, lista_.map((n) => n.wiersz));
        opIds.push(r.opId);
        poprawek += r.poprawek;
        dolozen += r.dolozen;
        usuniec += r.usuniec ?? 0;
      }
      setNaprawy({});
      setPotwierdzNaprawy(false);
      setBladNapraw(
        t('baza.naprawyWyslane').replace('{p}', String(poprawek)).replace('{d}', String(dolozen)).replace('{u}', String(usuniec)),
      );
      void qc.invalidateQueries({ queryKey: ['authoring', 'operations'] });
    } catch (e) {
      setBladNapraw(e instanceof ApiError || e instanceof Error ? e.message : t('common.error'));
    } finally {
      if (opIds.length > 0) {
        setWyslane(opIds);
        setBladWysylki(null);
      }
      setWysylaNaprawy(false);
    }
  };
  const liczbaNapraw = Object.keys(naprawy).length;
  /** Usunięcia liczone osobno — potwierdzenie mówi, ile wierszy zniknie z całej wyspy. */
  const liczbaUsuniec = Object.values(naprawy).filter((n) => n.usuwa === true).length;
  /** Kolejność wierszy samego wyniku — dla edycji i eksportu (sortowanie po stanie ich nie dotyczy). */
  const kolejnoscWynikuTu = wynik === null ? [] : kolejnoscWyniku(wynik.wiersze, wynik.kolumny, sort);
  const ikonaSortu = (tenNaglowek: boolean): React.ReactNode =>
    tenNaglowek && sort !== null ? (sort.malejaco ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />) : null;
  const ariaSortu = (tenNaglowek: boolean): 'ascending' | 'descending' | 'none' =>
    tenNaglowek && sort !== null ? (sort.malejaco ? 'descending' : 'ascending') : 'none';
  const sortowanaKolumna = wynik === null ? null : kolumnaSortowania(sort, wynik.kolumny);

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">{t('baza.baza')}</span>
        <Select value={agentId} onChange={(e) => { setAgentId(e.target.value); setTu(null); setTam(null); setEdycja(false); }} className="h-8 w-auto text-xs">
          {lista.map((w) => (
            <option key={w.id} value={w.id} disabled={!w.obsluguje}>
              {opisWezla(w, t)}
            </option>
          ))}
        </Select>
        <span className="ml-2 text-muted-foreground">{t('baza.porownaj')}</span>
        <Select value={porownanieZ} onChange={(e) => { setPorownanieZ(e.target.value); setTam(null); setNaprawy({}); }} className="h-8 w-auto text-xs">
          <option value="">—</option>
          {lista
            .filter((w) => w.id !== agentId && w.obsluguje)
            .map((w) => (
              <option key={w.id} value={w.id}>
                {opisWezla(w, t)}
              </option>
            ))}
        </Select>
      </div>

      <div className="grid gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <DrzewoTabel
          tabele={tabele}
          laduje={schemat.isFetching}
          blad={schemat.isError ? (schemat.error as Error).message : null}
          onTop={(x) => {
            const q = top1000(x);
            setSql(q);
            void wykonaj(q);
          }}
          onWstaw={wstaw}
          onOdswiez={() => void schemat.refetch()}
        />

        <div className="min-w-0 space-y-2">
          {/* ⚠️ Podpowiedzi nazw z drzewa wybranej bazy; F5 i Ctrl+Enter wykonują — `EdytorSql.tsx`. */}
          <EdytorSql
            value={sql}
            onChange={setSql}
            onWykonaj={() => void wykonaj()}
            tabele={tabele}
            placeholder={t('baza.placeholder')}
            poleRef={edytor}
          />
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Button size="sm" disabled={czeka || sql.trim() === '' || wybrany === null} onClick={() => void wykonaj()}>
              {czeka ? <Loader2 className="animate-spin" /> : <Play className="size-3.5" />}
              {t('baza.wykonaj')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={wynik === null}
              onClick={() =>
                wynik !== null &&
                pobierzCsv(
                  /** Eksport w kolejności z ekranu — po sortowaniu człowiek oczekuje tego, co widzi. */
                  { ...wynik, wiersze: kolejnoscWynikuTu.map((i) => wynik.wiersze[i]!) },
                  `zapytanie-${tu?.nodeId ?? 'baza'}-${new Date().toISOString().slice(0, 10)}.csv`,
                )
              }
            >
              <Download className="size-3.5" />
              {t('baza.eksport')}
            </Button>
            {/*
              ⚠️ EDYCJA TYLKO DLA WYNIKU Z JEDNEJ TABELI Z KATALOGU I Z JEJ KLUCZEM — bez klucza wiersza
              nie da się wskazać w pozostałych bazach. Powód braku stoi w dymku, a nie w ciszy.
            */}
            {wynik !== null && !edycja && (
              <Button
                size="sm"
                variant="outline"
                disabled={powodBrakuEdycjiWyniku !== null}
                title={powodBrakuEdycjiWyniku ?? undefined}
                onClick={() => setEdycja(true)}
              >
                <Pencil className="size-3.5" />
                {t('baza.edytuj')}
              </Button>
            )}
            {czeka && (
              <span className="text-muted-foreground">{t('baza.czekam').replace('{wezel}', [wybrany?.nodeId, drugi?.nodeId].filter(Boolean).join(', '))}</span>
            )}
            {wynik !== null && (
              <span className="text-muted-foreground">
                {t('baza.wierszy')
                  .replace('{n}', String(wynik.wiersze.length))
                  .replace('{ms}', String(wynik.ms))
                  .replace('{wezel}', tu?.nodeId ?? '')}
              </span>
            )}
            {wynik?.obcieto === true && <Badge variant="warning">{t('baza.obcieto').replace('{n}', String(wynik.wiersze.length))}</Badge>}
          </div>

          {bladZlecenia !== null && <p className="text-xs text-[var(--destructive)]">{bladZlecenia}</p>}
          {/* ⚠️ Stan na węzłach po wysyłce — kto przyjął, kto odmówił i dlaczego; `StanWysylki.tsx`. */}
          {wyslane.length > 0 && <StanWysylki groupId={groupId} opIds={wyslane} onOdswiez={() => void wykonaj(sqlWyniku)} />}
          {bladWysylki !== null && <p className="text-xs text-[var(--destructive)]">{bladWysylki}</p>}
          {tu?.blad !== undefined && <p className="text-xs text-[var(--destructive)]">{tu.nodeId}: {tu.blad}</p>}
          {tam?.blad !== undefined && <p className="text-xs text-[var(--destructive)]">{tam.nodeId}: {tam.blad}</p>}

          {/*
            ⚠️ PODSUMOWANIE PORÓWNANIA NAD SIATKĄ — liczby mówią od razu, czy jest rozjazd,
            zanim ktokolwiek przewinie tysiąc wierszy.
          */}
          {!edycja && porownanie !== null && tam !== null && (
            <p className="text-xs">
              {porownanie.ok ? (
                <>
                  <span className="font-medium">
                    {t('baza.porownanie')
                      .replace('{wezel}', tam.nodeId)
                      .replace('{z}', String(porownanie.liczby.ZGODNY))
                      .replace('{i}', String(porownanie.liczby.INNY))
                      .replace('{t}', String(porownanie.liczby.TYLKO_TU))
                      .replace('{b}', String(porownanie.liczby.BRAK_TU))}
                  </span>{' '}
                  <span className="text-muted-foreground">({porownanie.poKluczu ? t('baza.poKluczu') : t('baza.poWierszach')})</span>
                  {porownanie.niepelne && <span className="ml-2 text-[var(--warning)]">{t('baza.niepelne')}</span>}
                  {mozeNaprawiac && (
                    <span className="ml-2 inline-flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => wybierzWszystkie('DO_DRUGIEJ')}>
                        {t('baza.wszystkieWg').replace('{wezel}', drugi!.nodeId)}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => wybierzWszystkie('Z_TEJ')}>
                        {t('baza.wszystkieWg').replace('{wezel}', wybrany!.nodeId)}
                      </Button>
                    </span>
                  )}
                </>
              ) : (
                <span className="text-[var(--warning)]">{t('baza.inneKolumny')}</span>
              )}
            </p>
          )}

          {wynik !== null && edycja && tabelaKatalogu !== null && (
            <SiatkaEdycji
              groupId={groupId}
              wynik={wynik}
              tabela={tabelaKatalogu}
              wezly={lista}
              kolejnosc={kolejnoscWynikuTu}
              onZakoncz={() => setEdycja(false)}
              onWyslano={(opIds, blad) => {
                setEdycja(false);
                setWyslane(opIds);
                setBladWysylki(blad);
                void qc.invalidateQueries({ queryKey: ['authoring', 'operations'] });
              }}
            />
          )}

          {/*
            ⚠️ NAPRAWY Z PORÓWNANIA NAD SIATKĄ: liczba, droga rozsyłki i wysyłka drugim kliknięciem.
            Każda naprawa idzie na CAŁĄ wyspę — master pierwszy — z kierunkiem wybranym przy wierszu.
          */}
          {mozeNaprawiac && (liczbaNapraw > 0 || bladNapraw !== null) && (
            <div className="space-y-2 rounded-md border border-border p-2 text-xs">
              {liczbaNapraw > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{t('baza.naprawyDoWyslania').replace('{n}', String(liczbaNapraw))}</span>
                  <span className="text-muted-foreground">{t('baza.pojdzie')}</span>
                  {drogaRozsylki(lista).map((d) => (
                    <span key={d.nodeId} className="rounded border border-border px-1.5 py-0.5 font-mono">
                      {d.krok} · {d.nodeId}
                    </span>
                  ))}
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" disabled={wysylaNaprawy} onClick={() => { setNaprawy({}); setPotwierdzNaprawy(false); }}>
                      {t('baza.wyczysc')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={wysylaNaprawy}
                      onClick={() => (potwierdzNaprawy ? void wyslijNaprawy() : setPotwierdzNaprawy(true))}
                    >
                      {wysylaNaprawy && <Loader2 className="animate-spin" />}
                      {potwierdzNaprawy
                        ? liczbaUsuniec > 0
                          ? t('baza.potwierdzNaprawyUsun').replace('{n}', String(liczbaNapraw)).replace('{u}', String(liczbaUsuniec))
                          : t('baza.potwierdzNaprawy').replace('{n}', String(liczbaNapraw))
                        : t('baza.wyslijNaprawy')}
                    </Button>
                  </span>
                </div>
              )}
              {bladNapraw !== null && <p className="text-muted-foreground">{bladNapraw}</p>}
            </div>
          )}

          {wynik !== null && !edycja && (
            <div className="max-h-[36rem] overflow-auto rounded-md border border-border">
              <table className="w-max min-w-full border-collapse text-xs">
                <thead className="sticky top-0 z-10 bg-card">
                  <tr>
                    <th className="border-b border-border px-2 py-1 text-right font-normal text-muted-foreground">#</th>
                    {/*
                      ⚠️ NAGŁÓWEK SORTUJE: rosnąco → malejąco → kolejność z bazy (`nastepneSortowanie`).
                      „Stan" przy porównaniu stawia rozjazdy na górze — po to się go klika.
                    */}
                    {porownanie?.ok === true && (
                      <th aria-sort={ariaSortu(sort?.kolumna === 'STAN')} className="border-b border-border px-2 py-1 text-left font-medium">
                        <button
                          type="button"
                          title={t('baza.sortuj')}
                          onClick={() => setSort((s) => nastepneSortowanie(s, 'STAN'))}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          {t('baza.stan')}
                          {ikonaSortu(sort?.kolumna === 'STAN')}
                        </button>
                      </th>
                    )}
                    {mozeNaprawiac && <th className="border-b border-border px-2 py-1 text-left font-medium">{t('baza.naprawa')}</th>}
                    {wynik.kolumny.map((k, i) => (
                      <th
                        key={`${k.nazwa}-${i}`}
                        title={k.typ}
                        aria-sort={ariaSortu(sortowanaKolumna === i)}
                        className="border-b border-border px-2 py-1 text-left font-medium"
                      >
                        <button
                          type="button"
                          onClick={() => setSort((s) => nastepneSortowanie(s, { kolumna: i, nazwa: k.nazwa }))}
                          className="flex items-center gap-1 whitespace-nowrap hover:text-foreground"
                        >
                          {k.nazwa === '' ? `(${i + 1})` : k.nazwa}
                          {ikonaSortu(sortowanaKolumna === i)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kolejnoscWidoku.slice(0, WIERSZY_W_SIATCE).map((r, poz) => {
                    const w = wierszeWidoku[r]!;
                    return (
                      <tr key={r} className={cn('border-b border-border/50', porownanie?.ok === true && KOLOR_STANU[w.stan])}>
                        <td className="px-2 py-0.5 text-right tabular-nums text-muted-foreground">{poz + 1}</td>
                        {porownanie?.ok === true && (
                          <td className="whitespace-nowrap px-2 py-0.5 text-muted-foreground">{t(`baza.stanWiersza.${w.stan}`)}</td>
                        )}
                        {mozeNaprawiac && (
                          <td className="whitespace-nowrap px-1 py-0.5">
                            {(() => {
                              const opcje = naprawyDla(w);
                              if (opcje.length === 0) {
                                const wl = w.stan !== 'ZGODNY' && tabelaSchematu?.automatu === true ? wlascicielWiersza(w, wynik, lista) : null;
                                return wl !== null && wl.id !== wybrany?.id && wl.id !== drugi?.id ? (
                                  <span className="text-[11px] text-muted-foreground">{t('baza.wzorzec').replace('{wezel}', wl.nodeId)}</span>
                                ) : null;
                              }
                              return opcje.map((o) => {
                                const wybrana = naprawy[r]?.kierunek === o.kierunek;
                                return (
                                  <button
                                    key={o.kierunek}
                                    type="button"
                                    title={t(o.usuwa === true ? 'baza.wgOpisUsun' : 'baza.wgOpis').replace('{wezel}', o.zrodlo.nodeId)}
                                    onClick={() => {
                                      setPotwierdzNaprawy(false);
                                      setNaprawy((p) => {
                                        const nast = { ...p };
                                        if (wybrana) delete nast[r];
                                        else nast[r] = o;
                                        return nast;
                                      });
                                    }}
                                    className={cn(
                                      'mr-1 rounded border px-1.5 text-[11px]',
                                      wybrana ? 'border-[var(--primary)] bg-[var(--primary)] text-[var(--primary-foreground)]' : 'border-border hover:bg-accent',
                                    )}
                                  >
                                    {o.usuwa === true && <Trash2 className="mr-0.5 inline size-3" aria-hidden />}
                                    {t('baza.wg').replace('{wezel}', o.zrodlo.nodeId)}
                                  </button>
                                );
                              });
                            })()}
                          </td>
                        )}
                        {w.wiersz.map((v, c) => {
                          const inna = 'rozne' in w && w.rozne?.includes(c) === true;
                          return (
                            <td
                              key={c}
                              title={inna && 'tam' in w ? t('baza.tam').replace('{v}', w.tam?.[c] ?? 'NULL') : undefined}
                              className={cn(
                                'max-w-[28rem] truncate whitespace-nowrap px-2 py-0.5 font-mono',
                                inna && 'bg-[var(--warning)]/25 font-medium',
                              )}
                            >
                              {v === null ? <span className="italic text-muted-foreground">NULL</span> : v}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
